/**
 * @file notificationPolicy.js
 * @description Notification delivery policy registry. Defines categories, deduplication
 *   key generators, and force-delivery overrides for each notification type.
 *   Used by notificationService to enforce consistent delivery rules.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/notificationPolicy
 */
export const NOTIFICATION_CATEGORIES = Object.freeze({
  messages: "messages",
  application_updates: "application_updates",
  interviews: "interviews",
  job_alerts: "job_alerts",
  new_applicants: "new_applicants",
  case_alerts: "case_alerts",
  platform: "platform",
  weekly_digest: "weekly_digest",
});

export const NOTIFICATION_POLICY = Object.freeze({
  placement: {
    category: NOTIFICATION_CATEGORIES.application_updates,
    dedupe: ({ link_data }) => link_data?.applicationId ? `placement:${link_data.applicationId}` : null,
    forceDelivery: true,
  },
  verification_request: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id, title, body }) => `verification_request:${user_id}:${title}:${body}`,
    forceDelivery: true,
  },
  verification_approved: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id }) => `verification_approved:${user_id}`,
    forceDelivery: true,
  },
  verification_rejected: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id, body }) => `verification_rejected:${user_id}:${body || ""}`,
    forceDelivery: true,
  },
  verification_expired: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id }) => `verification_expired:${user_id}`,
    forceDelivery: true,
  },
  verification_revoked: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id }) => `verification_revoked:${user_id}`,
    forceDelivery: true,
  },
  verification_expiring_soon: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id }) => `verification_expiring_soon:${user_id}`,
    forceDelivery: true,
  },
  listing_flagged: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id, body }) => `listing_flagged:${user_id}:${body || ""}`,
    forceDelivery: true,
  },
  platform_update: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: null,          // dedupe_key supplied externally by the broadcast function
    forceDelivery: false,  // respects the user's platform toggle
  },
  // An invite was accepted. Written by the server (trg_team_notify_joined) into the agency's
  // shared bell; listed here so the type has a category like every other.
  team_joined: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: null,          // dedupe_key team_joined:<member id> is written by the trigger
    forceDelivery: true,
  },
  // One-time discovery tip pointing therapists at the free Pay & Hours tracker
  // in their profile menu. Dedupe per user so it can only ever fire once.
  tracker_discovery: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id }) => `tracker_discovery:${user_id}`,
    forceDelivery: true,
  },

  // ── Application status transitions ─────────────────────────────────────────
  application_reviewing: {
    category: NOTIFICATION_CATEGORIES.application_updates,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,
  },
  application_interview: {
    category: NOTIFICATION_CATEGORIES.application_updates,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,
  },
  application_offered: {
    category: NOTIFICATION_CATEGORIES.application_updates,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,
  },
  application_declined: {
    category: NOTIFICATION_CATEGORIES.application_updates,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,
  },
  case_filled: {
    category: NOTIFICATION_CATEGORIES.application_updates,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,
  },
  listing_updated: {
    category: NOTIFICATION_CATEGORIES.application_updates,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: false,   // informational — respects user preference
  },

  // ── Messaging ───────────────────────────────────────────────────────────────
  new_message: {
    category: NOTIFICATION_CATEGORIES.messages,
    // sendMessage passes an explicit dedupe_key (which wins in
    // notificationService), so this is the fallback path. It reads `threadId`
    // first: messageService switched link_data to `threadId` for outreach-thread
    // support, and an applicationId-only lookup silently returned null here —
    // meaning any future caller that omits dedupe_key would get NO deduping and
    // ring the bell on every single message. applicationId is kept as a fallback
    // for the other services that still emit it.
    dedupe: ({ user_id, link_data }) => {
      const threadId = link_data?.threadId || link_data?.applicationId;
      return threadId ? `new_message:${user_id}:${threadId}` : null;
    },
    forceDelivery: true,
    // The dedupe_key is a standing "one bell per conversation" slot, not a
    // one-shot event id: once the existing row has been READ, the next message
    // re-arms it (create_notification_for_user, migration 20261014110000). While
    // it is still unread it stays deduped. MUST match v_rering_types in that
    // function; notificationRering.test.js reads the migration and fails on drift.
    reRing: true,
  },

  // ── Admin / platform actions ────────────────────────────────────────────────
  bgc_cleared: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id }) => `bgc_cleared:${user_id}`,
    forceDelivery: true,
  },
  verification_pending: {
    category: NOTIFICATION_CATEGORIES.platform,
    dedupe: ({ user_id }) => `verification_pending:${user_id}`,
    forceDelivery: true,
  },

  // ── Interview lifecycle ─────────────────────────────────────────────────────
  interview_invite_sent: {
    category: NOTIFICATION_CATEGORIES.interviews,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,
  },
  interview_confirmed: {
    category: NOTIFICATION_CATEGORIES.interviews,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,
  },
  // Therapist responded to an interview invite (confirmed / declined / rescheduled)
  // Delivered to the AGENCY.
  interview_response_received: {
    category: NOTIFICATION_CATEGORIES.interviews,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,
  },

  // ── Case activity alerts (agency) ───────────────────────────────────────────
  case_expiring_soon: {
    category: NOTIFICATION_CATEGORIES.case_alerts,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: false,  // respects case_alerts pref
  },
  case_stale_alert: {
    category: NOTIFICATION_CATEGORIES.case_alerts,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: false,  // respects case_alerts pref
  },
  case_reminder_due: {
    category: NOTIFICATION_CATEGORIES.case_alerts,
    dedupe: ({ dedupe_key }) => dedupe_key || null,
    forceDelivery: true,   // the agency self-set this reminder — always deliver
  },
});

/**
 * @description Returns the policy object for a notification type, or null if unknown.
 * @param {string} type - Notification type key
 * @returns {Object|null} Policy object with category, dedupe, forceDelivery
 */
export function getNotificationPolicy(type) {
  return NOTIFICATION_POLICY[type] || null;
}

/**
 * @description True when a type's dedupe_key is a re-ringable per-conversation
 *   slot (a new event re-arms the row once it has been read). One-shot types
 *   (new_applicant, app_status_*, offer_response, case alerts...) return false.
 * @param {string} type - Notification type key
 * @returns {boolean}
 */
export function isReRingableNotification(type) {
  return getNotificationPolicy(type)?.reRing === true;
}

/**
 * @description Builds a deduplication key for a notification payload using the
 *   policy's dedupe function. Returns null if no deduplication applies.
 * @param {Object} [payload] - Notification payload
 * @returns {string|null} Deduplication key or null
 */
export function buildNotificationDedupeKey(payload = {}) {
  const policy = getNotificationPolicy(payload.type);
  if (!policy?.dedupe) return null;
  try {
    const key = policy.dedupe(payload);
    return key ? String(key).slice(0, 160) : null;
  } catch (_) {
    return null;
  }
}

/**
 * @description Returns true if a notification of the given type should be delivered
 *   given the user's preference settings. forceDelivery=true bypasses prefs.
 * @param {string} type - Notification type key
 * @param {Object} [prefs] - User notification preference object
 * @returns {boolean}
 */
export function shouldDeliverNotification(type, prefs = {}) {
  const policy = getNotificationPolicy(type);
  if (!policy) return true;
  if (policy.forceDelivery) return true;
  const category = policy.category;
  if (!category) return true;
  const value = prefs?.[category];
  return value !== false;
}
